import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './Projects.css';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: 'Modern Commercial Kitchen Design',
      category: 'industrial-design',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Complete kitchen design for a 5-star hotel with state-of-the-art equipment',
      client: 'Grand Hotel Chain',
      year: '2024',
      location: 'New York, USA'
    },
    {
      id: 2,
      title: 'MEP System Integration',
      category: 'mep-services',
      image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Comprehensive MEP design and installation for office complex',
      client: 'Corporate Plaza',
      year: '2024',
      location: 'California, USA'
    },
    {
      id: 3,
      title: 'Equipment Training Program',
      category: 'training',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Comprehensive training program for industrial equipment operators',
      client: 'Manufacturing Corp',
      year: '2023',
      location: 'Texas, USA'
    },
    {
      id: 4,
      title: 'Facility Maintenance Contract',
      category: 'maintenance',
      image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Long-term maintenance contract for shopping mall facilities',
      client: 'Metro Shopping Center',
      year: '2023',
      location: 'Florida, USA'
    },
    {
      id: 5,
      title: 'Industrial Laundry Setup',
      category: 'industrial-design',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Complete laundry facility design and equipment installation',
      client: 'Healthcare System',
      year: '2023',
      location: 'Illinois, USA'
    },
    {
      id: 6,
      title: 'Smart Building MEP Design',
      category: 'mep-services',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Advanced MEP systems for smart office building',
      client: 'Tech Tower',
      year: '2024',
      location: 'Washington, USA'
    },
    {
      id: 7,
      title: 'Safety Training Initiative',
      category: 'training',
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Comprehensive safety training for construction workers',
      client: 'Construction Alliance',
      year: '2023',
      location: 'Nevada, USA'
    },
    {
      id: 8,
      title: 'Preventive Maintenance Program',
      category: 'maintenance',
      image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Scheduled maintenance program for industrial equipment',
      client: 'Industrial Park',
      year: '2024',
      location: 'Ohio, USA'
    }
  ];

  const filters = [
    { key: 'all', label: 'All Projects' },
    { key: 'industrial-design', label: 'Industrial Design' },
    { key: 'mep-services', label: 'MEP Services' },
    { key: 'training', label: 'Training' },
    { key: 'maintenance', label: 'Maintenance' }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <div className="projects-page">
      {/* Hero Section */}
      <section className="projects-hero">
        <div className="hero-background">
          <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Our Projects" />
        </div>
        <div className="hero-overlay"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="hero-content"
          >
            <h1>Our Projects</h1>
            <p>Showcasing our successful implementations and satisfied clients</p>
          </motion.div>
        </div>
      </section>

      {/* Projects Filter */}
      <section className="projects-filter section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Featured Projects</h2>
            <p className="section-subtitle">
              Browse through our portfolio of successful projects across different categories
            </p>
          </motion.div>

          <div className="filter-buttons">
            {filters.map((filter) => (
              <button
                key={filter.key}
                className={`filter-btn ${activeFilter === filter.key ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="projects-grid-section">
        <div className="container">
          <div className="projects-grid">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="project-card"
                whileHover={{ y: -10 }}
              >
                <div className="project-image">
                  <img src={project.image} alt={project.title} />
                  <div className="project-overlay">
                    <div className="project-info">
                      <h3>{project.title}</h3>
                      <p>{project.description}</p>
                      <div className="project-details">
                        <div className="detail-item">
                          <i className="fas fa-user"></i>
                          <span>{project.client}</span>
                        </div>
                        <div className="detail-item">
                          <i className="fas fa-calendar"></i>
                          <span>{project.year}</span>
                        </div>
                        <div className="detail-item">
                          <i className="fas fa-map-marker-alt"></i>
                          <span>{project.location}</span>
                        </div>
                      </div>
                      <button className="project-btn">
                        View Details <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Stats */}
      <section className="project-stats section parallax-bg">
        <div className="parallax-overlay"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Project Statistics</h2>
            <p className="section-subtitle">
              Numbers that reflect our commitment to excellence
            </p>
          </motion.div>

          <div className="stats-grid">
            {[
              { number: '500+', label: 'Completed Projects', icon: 'fas fa-project-diagram' },
              { number: '200+', label: 'Happy Clients', icon: 'fas fa-smile' },
              { number: '50+', label: 'Ongoing Projects', icon: 'fas fa-cogs' },
              { number: '15+', label: 'Years Experience', icon: 'fas fa-calendar-alt' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="stat-card"
                whileHover={{ y: -10 }}
              >
                <div className="stat-icon">
                  <i className={stat.icon}></i>
                </div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="testimonials section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Client Testimonials</h2>
            <p className="section-subtitle">
              What our clients say about our work
            </p>
          </motion.div>

          <div className="testimonials-grid">
            {[
              {
                name: 'John Anderson',
                position: 'Facilities Manager',
                company: 'Grand Hotel Chain',
                image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
                testimonial: 'Efficient Group delivered an exceptional kitchen design that exceeded our expectations. Their attention to detail and professional approach made the entire process seamless.'
              },
              {
                name: 'Sarah Mitchell',
                position: 'Operations Director',
                company: 'Corporate Plaza',
                image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
                testimonial: 'The MEP system integration was flawless. The team demonstrated exceptional technical expertise and delivered the project on time and within budget.'
              },
              {
                name: 'Michael Chen',
                position: 'Plant Manager',
                company: 'Manufacturing Corp',
                image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
                testimonial: 'The training program significantly improved our operators\' skills and safety awareness. Highly professional and comprehensive approach to training.'
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="testimonial-card"
                whileHover={{ y: -5 }}
              >
                <div className="testimonial-content">
                  <div className="quote-icon">
                    <i className="fas fa-quote-left"></i>
                  </div>
                  <p>"{testimonial.testimonial}"</p>
                </div>
                <div className="testimonial-author">
                  <img src={testimonial.image} alt={testimonial.name} />
                  <div className="author-info">
                    <h4>{testimonial.name}</h4>
                    <p>{testimonial.position}</p>
                    <span>{testimonial.company}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="projects-cta parallax-bg">
        <div className="parallax-overlay"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2>Ready to Start Your Project?</h2>
            <p>Let's discuss how we can help you achieve your facility management goals</p>
            <div className="cta-buttons">
              <a href="/contact" className="btn btn-primary">
                Start Your Project
              </a>
              <a href="/services" className="btn btn-secondary">
                View Our Services
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Projects;